import { MantineProvider } from '@mantine/core';
import React from 'react';
import { createRoot } from 'react-dom/client';
import { Navigate, Outlet, RouterProvider, createBrowserRouter } from 'react-router';

import { Layout } from './app/layout';
import { ErrorBoundary } from './components/ErrorBoundary';
import { useGames, Provider as GamesProvider } from './data/games';
import * as remote from './data/remote-data';
import { GamePage } from './pages/GamePage';
import { AdControlTab } from './pages/GamePage/AdControlTab';
import { CampaignManagementTab } from './pages/GamePage/CampaignManagementTab';
import { CreativeRepositoryTab } from './pages/GamePage/CreativeRepositoryTab';
import { CreativesTab } from './pages/GamePage/CreativesTab';
import { ReportsTab } from './pages/GamePage/ReportsTab';
import { ROASTab } from './pages/GamePage/ROASTab';
import { LineUrlGeneratorTab } from './platforms/line/components/UrlGeneratorTab';
import { theme } from './theme';

const RedirectToFirstGame = () => {
    const games = useGames();
    return remote.matchSome(
        games,
        {
            Success: (list) => {
                const first = list[0];
                return first ? <Navigate to={`/games/${first.id}/campaigns`} replace /> : null;
            },
        },
        () => null,
    );
};

const router = createBrowserRouter([
    {
        id: 'root',
        path: '/',
        element: (
            <Layout>
                <Outlet />
            </Layout>
        ),
        loader: () => ({ title: 'Root' }),
        children: [
            {
                index: true,
                element: <RedirectToFirstGame />,
            },
            {
                path: 'games/:id',
                element: <GamePage />,
                children: [
                    {
                        index: true,
                        element: <Navigate to="campaigns" replace />,
                    },
                    {
                        path: 'campaigns',
                        element: <CampaignManagementTab />,
                    },
                    {
                        path: 'creatives',
                        element: <CreativeRepositoryTab />,
                    },
                    {
                        path: 'line-media',
                        element: <CreativesTab />,
                    },
                    {
                        path: 'reports',
                        element: <ReportsTab />,
                    },
                    {
                        path: 'roas',
                        element: <ROASTab />,
                    },
                    {
                        path: 'ad-control',
                        element: <AdControlTab />,
                    },
                    {
                        path: 'line-url-generator',
                        element: <LineUrlGeneratorTab />,
                    },
                ],
            },
        ],
    },
]);

createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
        <MantineProvider theme={theme} defaultColorScheme="auto">
            <ErrorBoundary>
                <GamesProvider>
                    <RouterProvider router={router} />
                </GamesProvider>
            </ErrorBoundary>
        </MantineProvider>
    </React.StrictMode>,
);
